import { useState, useRef, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { mensagensPublicAPI, publicAPI } from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Send, CheckCircle, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, List, ListOrdered } from 'lucide-react';

const ToolbarButton = ({ onClick, active, children, title }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={`p-1.5 rounded hover:bg-slate-200 ${active ? 'bg-slate-200 text-amber-700' : 'text-slate-600'}`}
    title={title}
  >
    {children}
  </button>
);

export const Recepcao = () => {
  const editorRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  usePageTitle('Receção');

  useEffect(() => {
    publicAPI.getLogo().then(res => {
      const url = res.data?.logo_url;
      if (url) setLogoUrl(url);
    }).catch(() => {});
  }, []);

  const execCommand = (command, value) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleInput = () => {
    const text = editorRef.current?.innerText?.trim() || '';
    setHasContent(text.length > 0);
  };

  const handleSubmit = async () => {
    const html = editorRef.current?.innerHTML || '';
    const text = editorRef.current?.innerText?.trim() || '';
    if (!text) return;

    setSending(true);
    try {
      await mensagensPublicAPI.create({ conteudo: html });
      setSent(true);
      editorRef.current.innerHTML = '';
      setHasContent(false);
      setTimeout(() => setSent(false), 4000);
    } catch (error) {
      alert('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="mx-auto mb-2" style={{ height: '15rem' }} data-testid="recepcao-logo" />
          ) : (
            <h1 className="text-2xl font-bold text-amber-700" data-testid="recepcao-title">
              Dr. Leonardo Pacheco
            </h1>
          )}
          <p className="text-slate-500 mt-1 text-xl">Enviar mensagem ao médico</p>
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardContent className="p-6 space-y-4">
            {sent && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700" data-testid="recepcao-success">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Mensagem enviada com sucesso!</span>
              </div>
            )}

            <div className="border border-slate-300 rounded-lg overflow-hidden" data-testid="recepcao-editor">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-0.5 p-2 bg-slate-50 border-b border-slate-300">
                <ToolbarButton onClick={() => execCommand('bold')} title="Negrito">
                  <Bold className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => execCommand('italic')} title="Itálico">
                  <Italic className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => execCommand('underline')} title="Sublinhado">
                  <Underline className="h-4 w-4" />
                </ToolbarButton>
                <div className="w-px h-5 bg-slate-300 mx-1" />
                <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Alinhar à esquerda">
                  <AlignLeft className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Centrar">
                  <AlignCenter className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => execCommand('justifyRight')} title="Alinhar à direita">
                  <AlignRight className="h-4 w-4" />
                </ToolbarButton>
                <div className="w-px h-5 bg-slate-300 mx-1" />
                <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Lista">
                  <List className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Lista numerada">
                  <ListOrdered className="h-4 w-4" />
                </ToolbarButton>
              </div>

              {/* Editor area */}
              <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                className="min-h-[200px] p-4 focus:outline-none text-slate-800 prose prose-sm max-w-none"
                data-placeholder="Escreva a sua mensagem aqui..."
                style={{ minHeight: '200px' }}
                data-testid="recepcao-editor-area"
              />
            </div>

            <style>{`
              [contenteditable]:empty:before {
                content: attr(data-placeholder);
                color: #9ca3af;
                pointer-events: none;
              }
            `}</style>

            <Button
              onClick={handleSubmit}
              disabled={sending || !hasContent}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12 text-base"
              data-testid="recepcao-submit-btn"
            >
              {sending ? (
                <span className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  A enviar...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Enviar Mensagem
                </span>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
